import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ImageList,
    ImageListItem,
    CircularProgress,
} from '@mui/material';
import {
    MdClose,
    MdExpandMore,
    MdCheckCircle,
    MdWarning,
    MdRemoveCircle,
    MdSpeed,
    MdPerson,
    MdDirectionsCar,
    MdCalendarToday,
    MdAssignment,
    MdPictureAsPdf,
} from 'react-icons/md';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { MdFolderZip } from 'react-icons/md';
import { CheckInspection } from '../../services/tipos';
import api from '../../services/api';

/**
 * Cada foto passa pela API do LSP e pela do Check até o MinIO; o caminho no storage é imutável, então vale guardar.
 * O cache tem teto: sem ele, cada vistoria aberta ou zipada deixava todos os blobs na memória até o fim da sessão.
 */
const fotosBaixadas = new Map<string, Promise<Blob>>();
export const LIMITE_FOTOS_EM_CACHE = 150;

export const baixarFoto = (filePath: string): Promise<Blob> => {
    let pendente = fotosBaixadas.get(filePath);
    if (!pendente) {
        const encodedPath = filePath.split('/').map(s => encodeURIComponent(s)).join('/');
        pendente = api.get(`/LocaSmartCheck/Fotos/${encodedPath}`, { responseType: 'blob' }).then(r => r.data as Blob);
        pendente.catch(() => fotosBaixadas.delete(filePath));
        fotosBaixadas.set(filePath, pendente);
        // Map preserva a ordem de inserção: descarta as mais antigas quando passa do teto.
        while (fotosBaixadas.size > LIMITE_FOTOS_EM_CACHE) {
            const maisAntiga = fotosBaixadas.keys().next().value;
            if (maisAntiga === undefined) break;
            fotosBaixadas.delete(maisAntiga);
        }
    }
    return pendente;
};

const AuthenticatedImage: React.FC<{ filePath: string; alt: string; style?: React.CSSProperties }> = ({ filePath, alt, style }) => {
    const [src, setSrc] = React.useState<string>('');
    React.useEffect(() => {
        let ativo = true;
        let objectUrl = '';
        baixarFoto(filePath)
            .then(blob => {
                if (!ativo) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            })
            .catch(() => {});
        return () => { ativo = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [filePath]);
    if (!src) return <CircularProgress size={24} />;
    return <img src={src} alt={alt} style={style} />;
};

const nomeSeguro = (texto: string) => (texto || '').replace(/[^\w.-]+/g, '_');

/** Zip com todas as fotos da vistoria, uma pasta por item (DL-171: "baixar o zip como tinha antes"). */
export const montarZipDasFotos = async (inspection: CheckInspection): Promise<Blob | null> => {
    const zip = new JSZip();
    let total = 0;
    for (const item of inspection.items || []) {
        for (const photo of item.photos || []) {
            const blob = await baixarFoto(photo.filePath);
            zip.file(`${nomeSeguro(item.templateItemName)}/${nomeSeguro(photo.fileName || photo.filePath.split('/').pop() || 'foto')}`, blob);
            total++;
        }
    }
    return total === 0 ? null : zip.generateAsync({ type: 'blob' });
};

interface ModalVisualizarVistoriaProps {
    open: boolean;
    onClose: () => void;
    inspection: CheckInspection | null;
}

function getStatusChip(status: string) {
    switch (status) {
        case 'Completed':
            return <Chip label="Concluída" size="small" sx={{ backgroundColor: '#4CAF50', color: 'white', fontWeight: 'bold' }} />;
        case 'InProgress':
            return <Chip label="Em Andamento" size="small" sx={{ backgroundColor: '#2196F3', color: 'white', fontWeight: 'bold' }} />;
        case 'AwaitingAnalysis':
            return <Chip label="Aguardando Análise" size="small" sx={{ backgroundColor: '#FFC107', color: 'white', fontWeight: 'bold' }} />;
        case 'Canceled':
            return <Chip label="Cancelada" size="small" sx={{ backgroundColor: '#f44336', color: 'white', fontWeight: 'bold' }} />;
        default:
            return <Chip label={status} size="small" />;
    }
}

function getItemStatusIcon(status: string) {
    switch (status) {
        case 'Ok':
        case 'OK':
            return <MdCheckCircle style={{ color: '#4CAF50', fontSize: 24 }} />;
        case 'Damage':
        case 'Avaria':
            return <MdWarning style={{ color: '#f44336', fontSize: 24 }} />;
        case 'NotApplicable':
        case 'NA':
            return <MdRemoveCircle style={{ color: '#9E9E9E', fontSize: 24 }} />;
        default:
            return null;
    }
}

function getItemStatusLabel(status: string) {
    switch (status) {
        case 'Ok':
        case 'OK': return 'OK';
        case 'Damage':
        case 'Avaria': return 'Avaria';
        case 'NotApplicable':
        case 'NA': return 'N/A';
        default: return status;
    }
}

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function ModalVisualizarVistoria({ open, onClose, inspection }: ModalVisualizarVistoriaProps) {
    // Hooks antes do return condicional (regra dos hooks).
    const [baixandoZip, setBaixandoZip] = React.useState(false);
    const temFotos = (inspection?.items || []).some(item => (item.photos || []).length > 0);
    if (!inspection) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                    Detalhes da Vistoria
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Header info */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: 2,
                    mb: 3,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <MdDirectionsCar style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Veículo</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {inspection.vehiclePlate} - {inspection.vehicleModel}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <MdPerson style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Motorista</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {inspection.driverName || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <MdPerson style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Vistoriador</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {inspection.inspectorName || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <MdCalendarToday style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Data</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {formatDate(inspection.completedAt || inspection.createdAt)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <MdSpeed style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Hodômetro</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {inspection.mileage ? `${inspection.mileage.toLocaleString('pt-BR')} km` : '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <MdAssignment style={{ color: '#1976d2', fontSize: 24 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Modelo</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {inspection.templateName || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box mt={0.5}>
                                {getStatusChip(inspection.status)}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Notes */}
                {inspection.notes && (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Observações Gerais
                        </Typography>
                        <Typography variant="body2">{inspection.notes}</Typography>
                    </Box>
                )}

                {/* Inspection items */}
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Itens da Vistoria
                </Typography>

                {inspection.items && inspection.items.length > 0 ? (
                    inspection.items.map((item, index) => (
                        <Accordion key={item.id || index} defaultExpanded={item.status === 'Avaria'} TransitionProps={{ unmountOnExit: true }}>
                            <AccordionSummary expandIcon={<MdExpandMore />}>
                                <Box display="flex" alignItems="center" gap={1.5} width="100%">
                                    {getItemStatusIcon(item.status)}
                                    <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                                        {item.templateItemName}
                                    </Typography>
                                    <Chip
                                        label={getItemStatusLabel(item.status)}
                                        size="small"
                                        sx={{
                                            backgroundColor: item.status === 'OK' ? '#E8F5E9' :
                                                item.status === 'Avaria' ? '#FFEBEE' : '#F5F5F5',
                                            color: item.status === 'OK' ? '#2E7D32' :
                                                item.status === 'Avaria' ? '#C62828' : '#616161',
                                            fontWeight: 'bold',
                                        }}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {item.notes && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Observações
                                        </Typography>
                                        <Typography variant="body2">{item.notes}</Typography>
                                    </Box>
                                )}

                                {item.photos && item.photos.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Fotos
                                        </Typography>
                                        <ImageList cols={3} rowHeight={160} gap={8}>
                                            {item.photos.map((photo) => (
                                                <ImageListItem key={photo.id}>
                                                    <AuthenticatedImage
                                                        filePath={photo.filePath}
                                                        alt={photo.fileName}
                                                        style={{
                                                            borderRadius: 8,
                                                            objectFit: 'cover',
                                                            width: '100%',
                                                            height: '100%',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    </Box>
                                )}

                                {!item.notes && (!item.photos || item.photos.length === 0) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhuma observação ou foto registrada para este item.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        Nenhum item registrado nesta vistoria.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Box display="flex" gap={1}>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<MdPictureAsPdf />}
                    onClick={async () => {
                        try {
                            const response = await api.get(`/LocaSmartCheck/Relatorio/${inspection.id}`, { responseType: 'blob' });
                            const blobUrl = URL.createObjectURL(response.data);
                            window.open(blobUrl, '_blank');
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                        } catch {
                            // silently fail
                        }
                    }}
                >
                    Ver Relatório
                </Button>
                <Button
                    variant="outlined"
                    startIcon={baixandoZip ? <CircularProgress size={16} /> : <MdFolderZip />}
                    disabled={baixandoZip || !temFotos}
                    onClick={async () => {
                        setBaixandoZip(true);
                        try {
                            const zip = await montarZipDasFotos(inspection);
                            if (zip) saveAs(zip, `vistoria_${nomeSeguro(inspection.vehiclePlate)}_${(inspection.completedAt || inspection.createdAt || '').substring(0, 10)}.zip`);
                        } catch {
                            // a falha de uma foto não derruba a tela; o usuário tenta de novo
                        } finally {
                            setBaixandoZip(false);
                        }
                    }}
                >
                    Baixar fotos (zip)
                </Button>
                </Box>
                <Button variant="outlined" onClick={onClose}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ModalVisualizarVistoria;
